import { alphabeticalSort } from '@semios/app-platform-common'
import type { TCsvHeaderMap, TCsvRowMap } from 'App/data-export/types/types'

const orderStringRecordsBy = <T extends Record<string, string>>(
  collection: T[],
  iteratees: Array<[keyof T, 'asc' | 'desc']>,
): T[] => {
  const newArray = [...collection]

  newArray.sort((a: T, b: T) => {
    for (let i = 0; i < iteratees.length; i++) {
      const [key, sortOrder] = iteratees[i]
      const comparisonResult = alphabeticalSort(a[key], b[key])

      if (comparisonResult !== 0) {
        return sortOrder === 'asc' ? comparisonResult : comparisonResult * -1
      }
      // If values are equal based on this key, continue to the next key
    }

    return 0 // All iteratees resulted in equal values
  })

  return newArray
}

const sanitizeForCsv = (str: string): string => {
  // Check for characters that require quoting: comma, double quote, newline, carriage return
  if (/[",\n\r]/.test(str)) {
    // Enclose in double quotes and escape existing double quotes
    return `"${str.replace(/"/g, '""')}"`
  }

  return str
}

export const makeCsvString = ({
  csvHeaderMap,
  csvRowMap,
  headerOrder,
  rowsOrderBy,
}: {
  csvHeaderMap: TCsvHeaderMap
  csvRowMap: TCsvRowMap
  headerOrder: Record<string, number>
  rowsOrderBy: Array<[string, 'asc' | 'desc']>
}): string => {
  const header: Record<string, string> = Object.fromEntries(csvHeaderMap.entries())

  const headerKeys = Object.keys(header).sort((a, b) => {
    return (headerOrder[a] || 9999) - (headerOrder[b] || 9999)
  })

  const rowData: Array<Record<string, string>> = Array.from(csvRowMap.values()).map((value) => ({
    ...Array.from(value.entries()).reduce<Record<string, string>>((a, b) => {
      return { ...a, [b[0]]: b[1] }
    }, {}),
  }))

  const sortedRowData = orderStringRecordsBy(rowData, rowsOrderBy)

  return [header, ...sortedRowData].reduce<string>((acc, cell) => {
    const row = headerKeys.map((headerKey) => sanitizeForCsv(cell[headerKey])).join(',')

    return `${acc}${row}\r\n`
  }, '')
}
